/**
 * NiV Dashboard — the single SaaS-admin workspace (mobile-first).
 *
 *  - URL-addressable tabs (/dashboard/niv/:tab[/:sub]) + last-tab memory
 *  - Desktop grouped side rail / tablet strip / mobile bottom bar + "more" sheet
 *  - Global clinic quick-find ("/" focuses it) → clinic detail page
 *  - Live badges per tab (action items, waiting tickets, open tasks, errors)
 *  - Tabs: briefing, clinics, clients, tasks, support, telemetry, data,
 *    errors, tools (push / system messages / system updates / monthly report / info)
 *
 * IMPORTANT: restricted to NiV admins (Firebase custom claim nivAdmin=true).
 * Menu visibility is gated in DashboardLayout; data access is gated by
 * firestore.rules (isNivAdmin()). The unauthorized view exposes the one-time
 * claim bootstrap (UID verified server-side by bootstrapNivAdmin).
 */

import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import {
  ArrowClockwise,
  Buildings,
  Bug,
  CaretLeft,
  ChartDonut,
  Crown,
  DotsThreeOutline,
  Headset,
  ChatsCircle,
  House,
  Kanban,
  MagnifyingGlass,
  Prohibit,
  Pulse,
  ShieldCheck,
  User,
  UsersThree,
  Wrench,
  X,
} from "@phosphor-icons/react";
import LoaderLoop from "../../../components/shared/loader/LoaderLoop";
import { selectIsNivAdmin, setNivAdmin } from "../../../store/slices/authSlice";
import { functions } from "../../../firebase";
import {
  nivApi,
  useGetNivClinicsOverviewQuery,
  useGetCallRequestsQuery,
  useGetMonitoringAlertsQuery,
  useGetLennyReviewsQuery,
  useGetProspectsBoardQuery,
  useGetNivTasksQuery,
  useGetNivTasksConfigQuery,
  useGetErrorLogsQuery,
  useGetNivInboxQuery,
} from "../../../store/services/nivApi";
import {
  nivSupportApi,
  useGetAllSupportTicketsQuery,
} from "../../../store/services/nivSupportApi";
import { CALL_REQUEST_STATUSES } from "../../../services/nivFirebase";
import { SUPPORT_TICKET_STATUSES } from "../../../services/supportFirebase";
import { useToast } from "../../../contexts/ToastContext";
import { buildStaleApprovedQueue } from "./shared/nivDerived";
import ClinicTags from "./shared/ClinicTags";
import MorningBriefing from "./home/MorningBriefing";
import NivClinicsTab from "./clinics/NivClinicsTab";
import NivClientsPage from "./clients/NivClientsPage";
import NivTasksPage from "./NivTasksPage";
import NivSupportPage from "./NivSupportPage";
import NivTelemetryTab from "./analytics/NivTelemetryTab";
import NivDataTab from "./analytics/NivDataTab";
import NivErrorLogsTab, { DEFAULT_ERROR_LOG_DAYS } from "./analytics/NivErrorLogsTab";
import NivToolsTab, { TOOL_SECTION_IDS, DEFAULT_TOOL_SECTION } from "./tools/NivToolsTab";
import NivMessagesTab, { MESSAGES_SECTION_IDS, DEFAULT_MESSAGES_SECTION, INBOX_QUERY_ARGS } from "./messages/NivMessagesTab";
import "./NivDashboardPage.css";
import "./NivDashboardShell.css";

export const NIV_BASE_PATH = "/dashboard/niv";

// Grouped for the desktop rail; flat order = tab strip order
const NAV_GROUPS = [
  {
    key: "home",
    title: "בית",
    items: [
      { id: "briefing", label: "בוקר טוב", icon: House },
      { id: "clinics", label: "קליניקות", icon: Buildings },
    ],
  },
  {
    key: "work",
    title: "עבודה",
    items: [
      // id stays "clients" — last-tab localStorage, badges and deep links depend on it
      { id: "clients", label: "לידים", icon: UsersThree },
      { id: "tasks", label: "משימות", icon: Kanban },
      { id: "support", label: "תמיכה", icon: Headset },
      { id: "messages", label: "הודעות", icon: ChatsCircle },
    ],
  },
  {
    key: "analytics",
    title: "אנליטיקה",
    items: [
      { id: "telemetry", label: "שימוש ומדדים", icon: Pulse },
      { id: "data", label: "נתונים", icon: ChartDonut },
      { id: "errors", label: "שגיאות", icon: Bug },
    ],
  },
  {
    key: "tools",
    title: "כלים",
    items: [{ id: "tools", label: "כלים", icon: Wrench }],
  },
];
const TABS = NAV_GROUPS.flatMap((g) => g.items);
const TAB_IDS = new Set(TABS.map((t) => t.id));
const TAB_BY_ID = Object.fromEntries(TABS.map((t) => [t.id, t]));
// Mobile bottom bar: 5 primary tabs + "more" sheet for the rest
const PRIMARY_MOBILE_TABS = ["briefing", "clinics", "clients", "tasks", "support"];
const MORE_TABS = TABS.filter((t) => !PRIMARY_MOBILE_TABS.includes(t.id));
const DEFAULT_TAB = "briefing";
const LAST_TAB_KEY = "niv.lastTab";
// Legacy standalone routes that now live as tabs
const LEGACY_PATH_TO_TAB = { tasks: "tasks", support: "support" };

const readLastTab = () => {
  try {
    const v = window.localStorage.getItem(LAST_TAB_KEY);
    return v && TAB_IDS.has(v) ? v : DEFAULT_TAB;
  } catch {
    return DEFAULT_TAB;
  }
};

const writeLastTab = (tab) => {
  try {
    window.localStorage.setItem(LAST_TAB_KEY, tab);
  } catch {
    /* private mode — ignore */
  }
};

const greetingForNow = () => {
  const h = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hour12: false,
      timeZone: "Asia/Jerusalem",
    }).format(new Date())
  );
  if (h < 5) return "לילה טוב";
  if (h < 12) return "בוקר טוב";
  if (h < 17) return "צהריים טובים";
  if (h < 21) return "ערב טוב";
  return "לילה טוב";
};

const todayLabel = () =>
  new Intl.DateTimeFormat("he-IL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Asia/Jerusalem",
  }).format(new Date());

const normalize = (s) => String(s || "").toLowerCase().replace(/[\s\-()]/g, "");

function NivDashboardPage() {
  const isAuthorized = useSelector(selectIsNivAdmin);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { tab: tabParam, sub: subParam } = useParams();
  const { showSuccess, showError } = useToast();

  // ---------- tab routing ----------
  const activeTab = tabParam && TAB_IDS.has(tabParam) ? tabParam : null;

  useEffect(() => {
    if (activeTab) {
      writeLastTab(activeTab);
      return;
    }
    // /dashboard/niv, a legacy standalone route, or an unknown tab → resolve
    const legacy = tabParam && LEGACY_PATH_TO_TAB[tabParam];
    navigate(`${NIV_BASE_PATH}/${legacy || readLastTab()}`, { replace: true });
  }, [activeTab, tabParam, navigate]);

  // Tools / messages sub-section lives in the URL so it is shareable / back-button friendly
  useEffect(() => {
    if (activeTab === "tools" && !TOOL_SECTION_IDS.has(subParam)) {
      navigate(`${NIV_BASE_PATH}/tools/${DEFAULT_TOOL_SECTION}`, { replace: true });
    }
    if (activeTab === "messages" && !MESSAGES_SECTION_IDS.has(subParam)) {
      navigate(`${NIV_BASE_PATH}/messages/${DEFAULT_MESSAGES_SECTION}`, { replace: true });
    }
  }, [activeTab, subParam, navigate]);

  const goToTab = useCallback(
    (id, state) => {
      navigate(`${NIV_BASE_PATH}/${id}`, state ? { state } : undefined);
    },
    [navigate]
  );

  // ---------- badge data (RTK dedupes with the tab components' own queries) ----------
  const { data: allRows = [] } = useGetNivClinicsOverviewQuery(undefined, {
    skip: !isAuthorized,
  });
  const rows = useMemo(() => allRows.filter((r) => !r.inactiveByAdmin), [allRows]);
  const { data: supportTickets = [] } = useGetAllSupportTicketsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: callRequests = [] } = useGetCallRequestsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: prospectsBoard } = useGetProspectsBoardQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: monitoringAlerts = [] } = useGetMonitoringAlertsQuery(undefined, {
    skip: !isAuthorized,
  });
  const { data: tasks = [] } = useGetNivTasksQuery(undefined, { skip: !isAuthorized });
  const { data: tasksConfig } = useGetNivTasksConfigQuery(undefined, {
    skip: !isAuthorized,
  });
  // Lenny (AI support) exchanges waiting for a human look
  const { data: lennyReviews = [] } = useGetLennyReviewsQuery(
    { status: "open" },
    { skip: !isAuthorized }
  );
  const openLennyReviewsCount = lennyReviews.length;
  // Error-log window is shared with the errors tab so the badge and the list agree
  const [errorLogDays, setErrorLogDays] = useState(DEFAULT_ERROR_LOG_DAYS);
  const { data: errorLogs = [] } = useGetErrorLogsQuery(
    { days: errorLogDays },
    { skip: !isAuthorized }
  );

  const waitingTicketsCount = useMemo(
    () => supportTickets.filter((t) => t.status === SUPPORT_TICKET_STATUSES.WAITING_ADMIN).length,
    [supportTickets]
  );
  const pendingCallsCount = useMemo(
    () => (callRequests || []).filter((r) => r.status === CALL_REQUEST_STATUSES.PENDING).length,
    [callRequests]
  );
  const staleApprovedCount = useMemo(
    () => buildStaleApprovedQueue(prospectsBoard?.prospects).length,
    [prospectsBoard]
  );
  // Clinic health cards (one per clinic) — only critical/warning count as work
  const importantAlertsCount = useMemo(
    () =>
      (monitoringAlerts || []).filter(
        (a) => a.severity === "critical" || a.severity === "warning"
      ).length,
    [monitoringAlerts]
  );
  const openTasksCount = useMemo(() => {
    const doneIds = new Set(
      (tasksConfig?.options?.status || []).filter((o) => o.isDone === true).map((o) => o.id)
    );
    return tasks.filter((t) => !doneIds.has(t.status)).length;
  }, [tasks, tasksConfig]);
  const unhandledErrorsCount = useMemo(
    () => errorLogs.filter((e) => e.status !== "handled").length,
    [errorLogs]
  );
  // Unread WhatsApp replies (niv_inbox) — replaces the old "reply → SMS" alert.
  // Shares the inbox panel's cache entry; polls so the badge stays fresh while
  // NiV works in other tabs.
  const { data: inbox } = useGetNivInboxQuery(INBOX_QUERY_ARGS, {
    skip: !isAuthorized,
    pollingInterval: 120000,
  });
  const unreadInboxCount = inbox?.unreadCount || 0;

  const badges = {
    briefing:
      waitingTicketsCount +
      pendingCallsCount +
      staleApprovedCount +
      importantAlertsCount +
      openLennyReviewsCount,
    clients: pendingCallsCount,
    support: waitingTicketsCount + openLennyReviewsCount,
    tasks: openTasksCount,
    errors: unhandledErrorsCount,
    messages: unreadInboxCount,
  };
  const URGENT_BADGES = new Set(["briefing", "support", "errors"]);
  const moreBadge = MORE_TABS.reduce((sum, t) => sum + (badges[t.id] || 0), 0);
  const moreIsUrgent = MORE_TABS.some((t) => URGENT_BADGES.has(t.id) && (badges[t.id] || 0) > 0);

  // ---------- refresh ----------
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      dispatch(nivApi.util.resetApiState());
      dispatch(nivSupportApi.util.resetApiState());
      showSuccess("הנתונים רועננו");
    } catch (error) {
      console.error("[CK-9868] Error refreshing NiV dashboard:", {
        errorCode: "CK-9868",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה ברענון הנתונים");
    } finally {
      // resetApiState is sync; keep the spinner visible long enough to register
      setTimeout(() => setIsRefreshing(false), 600);
    }
  }, [dispatch, showSuccess, showError]);

  // ---------- nivAdmin claim bootstrap (unauthorized view only) ----------
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const handleBootstrap = useCallback(async () => {
    setIsBootstrapping(true);
    try {
      const bootstrap = httpsCallable(functions, "bootstrapNivAdmin");
      await bootstrap();
      const user = getAuth().currentUser;
      if (user) {
        await user.getIdToken(true);
        const tokenResult = await user.getIdTokenResult();
        dispatch(setNivAdmin(tokenResult.claims.nivAdmin === true));
      }
      showSuccess("הרשאת אדמין הוגדרה בהצלחה! הדף יתרענן...");
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("[CK-9891] NiV admin bootstrap failed:", {
        errorCode: "CK-9891",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("אין הרשאה - ה-UID שלך לא תואם");
    } finally {
      setIsBootstrapping(false);
    }
  }, [dispatch, showSuccess, showError]);

  // ---------- mobile "more" sheet ----------
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [activeTab]);

  // ---------- clinic quick-find ----------
  const [query, setQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);

  const results = useMemo(() => {
    const q = normalize(query);
    if (q.length < 2) return [];
    return rows
      .filter((r) => {
        return (
          normalize(r.name).includes(q) ||
          normalize(r.ownerName).includes(q) ||
          normalize(r.ownerPhone).includes(q) ||
          normalize(r.clinicId).includes(q)
        );
      })
      .slice(0, 8);
  }, [rows, query]);

  useEffect(() => setHighlight(0), [query]);

  const openClinic = useCallback(
    (clinicId) => {
      setQuery("");
      setIsSearchOpen(false);
      navigate(`${NIV_BASE_PATH}/clinic/${clinicId}`);
    },
    [navigate]
  );

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setQuery("");
    searchInputRef.current?.blur();
  }, []);

  // "/" focuses search, Esc closes search / more sheet — skip when typing in another field
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      const typing =
        tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (e.key === "/" && !typing) {
        e.preventDefault();
        setIsSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 0);
      } else if (e.key === "Escape") {
        if (isSearchOpen) closeSearch();
        if (moreOpen) setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isSearchOpen, moreOpen, closeSearch]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;
    const onDown = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isSearchOpen]);

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && results[highlight]) {
      openClinic(results[highlight].clinicId);
    }
  };

  // ---------- unauthorized ----------
  if (!isAuthorized) {
    return (
      <div className="niv-unauthorized">
        <Prohibit size={64} weight="duotone" />
        <h2>אין הרשאה</h2>
        <p>אין לך הרשאה לצפות בדף זה.</p>
        <button
          type="button"
          className="niv-bootstrap-btn"
          onClick={handleBootstrap}
          disabled={isBootstrapping}
        >
          {isBootstrapping ? (
            <>
              <LoaderLoop size="xs" /> מגדיר הרשאות...
            </>
          ) : (
            <>
              <ShieldCheck size={18} /> הפעל הרשאת אדמין
            </>
          )}
        </button>
      </div>
    );
  }

  if (!activeTab) return null; // redirecting

  const initialTicketId = location.state?.ticketId || null;

  const renderNavItem = (tab, variant) => {
    const isActive = activeTab === tab.id;
    const count = badges[tab.id] || 0;
    return (
      <button
        key={tab.id}
        type="button"
        className={`niv2-nav-item ${isActive ? "active" : ""}`}
        onClick={() => goToTab(tab.id)}
        aria-current={isActive ? "page" : undefined}
        aria-label={count > 0 ? `${tab.label} (${count})` : tab.label}
      >
        <span className="niv2-nav-icon">
          <tab.icon size={variant === "bottom" ? 22 : 20} weight={isActive ? "fill" : "regular"} />
          {count > 0 && (
            <span className={`niv2-nav-badge ${URGENT_BADGES.has(tab.id) ? "urgent" : ""}`}>
              {count > 99 ? "99+" : count}
            </span>
          )}
        </span>
        <span className="niv2-nav-label">{tab.label}</span>
      </button>
    );
  };

  const moreIsActive = MORE_TABS.some((t) => t.id === activeTab);

  return (
    <div className={`niv2-page niv2-tab-${activeTab}`}>
      {/* ===== Header ===== */}
      <header className="niv2-header">
        <div className="niv2-header-main">
          <div className="niv2-title">
            <Crown size={24} weight="fill" className="niv2-crown" />
            <div className="niv2-title-text">
              <h1>{greetingForNow()}, ניב</h1>
              <span className="niv2-date">{todayLabel()}</span>
            </div>
          </div>

          <div className="niv2-header-actions">
            <div
              ref={searchWrapRef}
              className={`niv2-search ${isSearchOpen ? "open" : ""}`}
              role="search"
            >
              <button
                type="button"
                className="niv2-icon-btn niv2-search-trigger"
                aria-label="חיפוש קליניקה"
                onClick={() => {
                  setIsSearchOpen(true);
                  setTimeout(() => searchInputRef.current?.focus(), 0);
                }}
              >
                <MagnifyingGlass size={20} weight="bold" />
              </button>
              <div className="niv2-search-field">
                <MagnifyingGlass size={16} className="niv2-search-icon" />
                <input
                  ref={searchInputRef}
                  type="search"
                  inputMode="search"
                  placeholder="חיפוש קליניקה, בעלים או טלפון…  ( / )"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setIsSearchOpen(true)}
                  onKeyDown={onSearchKeyDown}
                  aria-label="חיפוש קליניקה"
                />
                {(query || isSearchOpen) && (
                  <button
                    type="button"
                    className="niv2-search-clear"
                    onClick={closeSearch}
                    aria-label="סגור חיפוש"
                  >
                    <X size={14} weight="bold" />
                  </button>
                )}
              </div>
              {isSearchOpen && query.trim().length >= 2 && (
                <div className="niv2-search-results" role="listbox">
                  {results.length === 0 ? (
                    <div className="niv2-search-empty">לא נמצאו קליניקות</div>
                  ) : (
                    results.map((r, i) => (
                      <button
                        key={r.clinicId}
                        type="button"
                        role="option"
                        aria-selected={i === highlight}
                        className={`niv2-search-row ${i === highlight ? "is-highlight" : ""}`}
                        onMouseEnter={() => setHighlight(i)}
                        onClick={() => openClinic(r.clinicId)}
                      >
                        <div className="niv2-search-row-main">
                          <strong>{r.name || "ללא שם"}</strong>
                          <span className="niv2-search-row-sub">
                            <User size={11} weight="fill" /> {r.ownerName || "—"}
                          </span>
                        </div>
                        <ClinicTags row={r} compact />
                        <CaretLeft size={14} className="niv2-search-row-caret" />
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="niv2-icon-btn"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="רענון"
              title="רענון"
            >
              <ArrowClockwise size={20} className={isRefreshing ? "spinning" : ""} />
            </button>
          </div>
        </div>

        {/* Horizontal scroll tabs — tablet only (desktop uses rail, mobile uses bottom bar) */}
        <nav className="niv2-tabs-strip" aria-label="ניווט">
          {TABS.map((t) => renderNavItem(t, "strip"))}
        </nav>
      </header>

      <div className="niv2-body">
        {/* ===== Desktop rail (grouped) ===== */}
        <aside className="niv2-rail" aria-label="ניווט">
          {NAV_GROUPS.map((group) => (
            <div key={group.key} className="niv2-rail-group">
              <div className="niv2-rail-group-title">{group.title}</div>
              <div className="niv2-rail-list">{group.items.map((t) => renderNavItem(t, "rail"))}</div>
            </div>
          ))}
        </aside>

        {/* ===== Content ===== */}
        <main className="niv2-content" key={activeTab}>
          {activeTab === "briefing" && (
            <MorningBriefing
              isAuthorized={isAuthorized}
              onOpenTicket={(ticket) => goToTab("support", { ticketId: ticket.id })}
              onGoToClients={() => goToTab("clients")}
            />
          )}
          {activeTab === "clinics" && <NivClinicsTab isAuthorized={isAuthorized} />}
          {activeTab === "clients" && <NivClientsPage isAuthorized={isAuthorized} />}
          {activeTab === "tasks" && (
            <div className="niv2-embed">
              <NivTasksPage />
            </div>
          )}
          {activeTab === "support" && (
            <div className="niv2-embed">
              <NivSupportPage initialTicketId={initialTicketId} embedded />
            </div>
          )}
          {activeTab === "telemetry" && <NivTelemetryTab isAuthorized={isAuthorized} />}
          {activeTab === "data" && <NivDataTab isAuthorized={isAuthorized} />}
          {activeTab === "errors" && (
            <NivErrorLogsTab
              isAuthorized={isAuthorized}
              days={errorLogDays}
              onDaysChange={setErrorLogDays}
            />
          )}
          {activeTab === "messages" && (
            <NivMessagesTab
              isAuthorized={isAuthorized}
              section={subParam}
              onSectionChange={(sub) => navigate(`${NIV_BASE_PATH}/messages/${sub}`)}
            />
          )}
          {activeTab === "tools" && (
            <NivToolsTab
              isAuthorized={isAuthorized}
              section={subParam}
              onSectionChange={(sub) => navigate(`${NIV_BASE_PATH}/tools/${sub}`)}
            />
          )}
        </main>
      </div>

      {/* ===== Mobile bottom bar ===== */}
      <nav className="niv2-bottom-bar" aria-label="ניווט">
        {PRIMARY_MOBILE_TABS.map((id) => renderNavItem(TAB_BY_ID[id], "bottom"))}
        <button
          type="button"
          className={`niv2-nav-item ${moreIsActive || moreOpen ? "active" : ""}`}
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          aria-label={moreBadge > 0 ? `עוד (${moreBadge})` : "עוד"}
        >
          <span className="niv2-nav-icon">
            <DotsThreeOutline size={22} weight={moreIsActive || moreOpen ? "fill" : "regular"} />
            {moreBadge > 0 && (
              <span className={`niv2-nav-badge ${moreIsUrgent ? "urgent" : ""}`}>
                {moreBadge > 99 ? "99+" : moreBadge}
              </span>
            )}
          </span>
          <span className="niv2-nav-label">עוד</span>
        </button>
      </nav>

      {/* ===== Mobile "more" sheet ===== */}
      {moreOpen && (
        <>
          <div className="niv2-sheet-backdrop" onClick={() => setMoreOpen(false)} />
          <div className="niv2-sheet" role="dialog" aria-label="עוד">
            <div className="niv2-sheet-handle" />
            <div className="niv2-sheet-list">
              {MORE_TABS.map((t) => renderNavItem(t, "sheet"))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NivDashboardPage;
